import { observer } from 'mobx-react-lite'
import { useRef, useState } from 'react'
import classNames from 'classnames'
import {
  type ActionItem,
  FullPageWrapper,
  Icon,
  SpinnerLoader,
  Tooltip,
  Typography,
  CalendarDropdown,
  Button,
} from 'shared/ui'
import { formatSecondsToLargestUnit } from 'shared/lib/dayjs'
import { useElementWidth } from 'shared/hooks'
import { PERFORMANCE_METRICS_CONFIG } from 'entities/Analytics/lib/constants'
import { BreakdownFilter } from 'pages/analytics/ui/BreakdownFilter/BreakdownFilter'
import { AnalyticsHeader, TrendLabel, AnalyticsEmptyState } from 'pages/analytics/ui'

import {
  PerformanceAnalyticsPageProvider,
  usePerformanceAnalyticsPageContext,
} from 'pages/analytics/pages/performance/context/performanceAnalyticsPageContext'
import { ChartFilter } from 'pages/analytics/ui/ChartFilter'
import { PerformanceComparisonTable } from './ComparisonTable'
import styles from './styles.module.scss'
import { PerformanceBarChart, PerformanceLineChart } from './Chart'

export const PerformanceAnalyticsPageContent = observer(() => {
  const {
    calendarFilterStore,
    breakdownFilterStore,
    openExportModal,
    pageLoading,
    chartDataStore,
    chartFilterStore,
    chartLoading,
    comparisonTableStore,
  } = usePerformanceAnalyticsPageContext()
  const { clearAllFilters, hasAppliedFilters } = breakdownFilterStore
  const { getStatisticByKey, hasStatistic } = chartDataStore
  const [hiddenMetrics, setHiddenMetrics] = useState<Set<string>>(new Set())
  const filtersRowRef = useRef<HTMLDivElement | null>(null)
  const filtersRowWidth = useElementWidth(filtersRowRef)

  const exportActions: ActionItem[] = [
    {
      text: 'Export data',
      icon: 'download',
      onAction: openExportModal,
    },
  ]

  const handleToggleMetric = (metricKey: string) => {
    setHiddenMetrics((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(metricKey)) {
        newSet.delete(metricKey)
      } else {
        newSet.add(metricKey)
      }
      return newSet
    })
  }

  const avgTimeToFirstResponseStat = getStatisticByKey('avg_time_to_first_response')
  const avgTimeToResponseStat = getStatisticByKey('avg_time_to_response')
  const avgTimeToCloseStat = getStatisticByKey('avg_time_to_close')

  const renderContent = () => {
    if (pageLoading) {
      return <SpinnerLoader ariaLabel={'PerformanceAnalyticsPage'} />
    }

    if (!hasStatistic) {
      return <AnalyticsEmptyState />
    }

    return (
      <>
        <div className={styles.kpiSection}>
          <div
            className={classNames(styles.kpiCard, styles.kpiAvgTimeToFirstResponse)}
            onClick={() => handleToggleMetric('avg_time_to_first_response')}
          >
            <div
              className={styles.kpiColorBar}
              style={{
                backgroundColor: hiddenMetrics.has('avg_time_to_first_response')
                  ? 'var(--gray-10)'
                  : PERFORMANCE_METRICS_CONFIG['avg_time_to_first_response'].color,
              }}
            />
            <div className={styles.kpiCardContent}>
              <div className={styles.kpiItemHeader}>
                <Typography
                  variant={'body-rg-regular'}
                  color={'tertiary'}
                  ariaLabel={'AvgTimeToFirstResponseTitle'}
                >
                  Avg time to first response
                </Typography>
                <Tooltip
                  placement='top'
                  breakWord
                  label='The average time taken to provide the first response to a customer inquiry during the selected date range.'
                >
                  <Icon icon='question' fontSize={14} color={'var(--content-neutral-primary)'} />
                </Tooltip>
              </div>
              <Typography
                variant={'heading-lg'}
                color={'primary'}
                ariaLabel={'AvgTimeToFirstResponseValue'}
              >
                {formatSecondsToLargestUnit(avgTimeToFirstResponseStat?.value ?? 0)}
              </Typography>
              {avgTimeToFirstResponseStat?.trend && (
                <TrendLabel trend={avgTimeToFirstResponseStat.trend} displayMode='percent' />
              )}
            </div>
          </div>
          <div
            className={classNames(styles.kpiCard, styles.kpiAvgTimeToResponse)}
            onClick={() => handleToggleMetric('avg_time_to_response')}
          >
            <div
              className={styles.kpiColorBar}
              style={{
                backgroundColor: hiddenMetrics.has('avg_time_to_response')
                  ? 'var(--gray-10)'
                  : PERFORMANCE_METRICS_CONFIG['avg_time_to_response'].color,
              }}
            />
            <div className={styles.kpiCardContent}>
              <div className={styles.kpiItemHeader}>
                <Typography
                  variant={'body-rg-regular'}
                  color={'tertiary'}
                  ariaLabel={'AvgTimeToResponseTitle'}
                >
                  Avg time to response
                </Typography>
                <Tooltip
                  placement='top'
                  breakWord
                  label='The average time taken to respond to customer inquiries during the selected date range.'
                >
                  <Icon icon='question' fontSize={14} color={'var(--content-neutral-primary)'} />
                </Tooltip>
              </div>
              <Typography
                variant={'heading-lg'}
                color={'primary'}
                ariaLabel={'AvgTimeToResponseValue'}
              >
                {formatSecondsToLargestUnit(avgTimeToResponseStat?.value ?? 0)}
              </Typography>
              {avgTimeToResponseStat?.trend && (
                <TrendLabel trend={avgTimeToResponseStat.trend} displayMode='percent' />
              )}
            </div>
          </div>
          <div
            className={classNames(styles.kpiCard, styles.kpiAvgTimeToClose)}
            onClick={() => handleToggleMetric('avg_time_to_close')}
          >
            <div
              className={styles.kpiColorBar}
              style={{
                backgroundColor: hiddenMetrics.has('avg_time_to_close')
                  ? 'var(--gray-10)'
                  : PERFORMANCE_METRICS_CONFIG['avg_time_to_close'].color,
              }}
            />
            <div className={styles.kpiCardContent}>
              <div className={styles.kpiItemHeader}>
                <Typography
                  variant={'body-rg-regular'}
                  color={'tertiary'}
                  ariaLabel={'AvgTimeToCloseTitle'}
                >
                  Avg time to close
                </Typography>
                <Tooltip
                  placement='top'
                  breakWord
                  label='The average time taken to close customer inquiries during the selected date range.'
                >
                  <Icon icon='question' fontSize={14} color={'var(--content-neutral-primary)'} />
                </Tooltip>
              </div>
              <Typography
                variant={'heading-lg'}
                color={'primary'}
                ariaLabel={'AvgTimeToCloseValue'}
              >
                {formatSecondsToLargestUnit(avgTimeToCloseStat?.value ?? 0)}
              </Typography>
              {avgTimeToCloseStat?.trend && (
                <TrendLabel trend={avgTimeToCloseStat.trend} displayMode='percent' />
              )}
            </div>
          </div>
        </div>

        <div className={styles.chartSection}>
          <div className={styles.chartHeader}>
            <ChartFilter store={chartFilterStore} />
          </div>

          <div className={styles.chartArea}>
            {chartLoading ? (
              <SpinnerLoader ariaLabel={'PerformanceAnalyticsPage'} />
            ) : (
              <>
                {chartFilterStore.chartType === 'line' ? (
                  <PerformanceLineChart
                    chartDataStore={chartDataStore}
                    hiddenMetrics={hiddenMetrics}
                  />
                ) : (
                  <PerformanceBarChart
                    chartDataStore={chartDataStore}
                    hiddenMetrics={hiddenMetrics}
                  />
                )}
              </>
            )}
          </div>
        </div>

        {hasAppliedFilters && (
          <div className={styles.tableSection}>
            <PerformanceComparisonTable store={comparisonTableStore} />
          </div>
        )}
      </>
    )
  }

  return (
    <FullPageWrapper className={styles.page}>
      <AnalyticsHeader title='Performance' actions={exportActions} />

      <div className={styles.filtersRow} ref={filtersRowRef}>
        <CalendarDropdown store={calendarFilterStore} />
        <BreakdownFilter store={breakdownFilterStore} parentWidth={filtersRowWidth} />
        {hasAppliedFilters && (
          <Button
            contained='secondary'
            color={'var(--content-primary-secondary)'}
            typeBtn='text'
            text='Clear all'
            onClick={clearAllFilters}
          />
        )}
      </div>

      {renderContent()}
    </FullPageWrapper>
  )
})

export const PerformanceAnalyticsPage = () => (
  <PerformanceAnalyticsPageProvider>
    <PerformanceAnalyticsPageContent />
  </PerformanceAnalyticsPageProvider>
)
